import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import {
  ChevronLeft,
  Users,
  Award,
  BookOpen,
  Sparkles,
  Bot,
  TrendingUp,
  FileText,
  Calendar,
  Eye,
  ChevronDown,
  ChevronUp,
  Loader2,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
const API = `${API_BASE}/api`;

export default function InstructorAssessments() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('course');
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(courseId || '');
  const [expandedAssessment, setExpandedAssessment] = useState(null);
  const [detailedAssessment, setDetailedAssessment] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetchAssessments(selectedCourse);
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const fetchAssessments = async (courseId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/navi/instructor/assessments`, {
        params: { course_id: courseId },
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data.report);
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedAssessment = async (assessmentId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/navi/instructor/assessment/${assessmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailedAssessment(response.data.assessment);
    } catch (error) {
      console.error('Failed to fetch assessment detail:', error);
      toast.error('Failed to load assessment details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleAssessment = (assessmentId) => {
    if (expandedAssessment === assessmentId) {
      setExpandedAssessment(null);
      setDetailedAssessment(null);
    } else {
      setExpandedAssessment(assessmentId);
      fetchDetailedAssessment(assessmentId);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-400';
    if (score >= 6) return 'text-yellow-400';
    if (score >= 4) return 'text-orange-400';
    return 'text-red-400';
  };

  const getCompetencyBadge = (level) => {
    const colors = {
      'strong': 'bg-green-500/20 text-green-400 border-green-500/30',
      'developing': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'emerging': 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    return colors[level] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
  };

  return (
    <div className="min-h-screen bg-navigate-dark">
      {/* Header */}
      <header className="bg-navigate-surface border-b border-zinc-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <Bot className="h-6 w-6 text-[#1EBFB3]" />
                <h1 className="text-xl font-heading font-bold text-white">
                  NAVI Coaching Assessments
                </h1>
              </div>
              <p className="text-sm text-zinc-400 mt-1">
                Review learner coaching sessions and assessments
              </p>
            </div>
          </div>
          
          {/* Course Filter */}
          <div className="flex items-center gap-3">
            <label className="text-sm text-zinc-400">Filter by Course:</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-navigate-gold"
            >
              <option value="">All Courses</option>
              {courses.map((course) => (
                <option key={course.course_id} value={course.course_id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-navigate-gold animate-spin" />
          </div>
        ) : !report || report.total_learners === 0 ? (
          <div className="text-center py-20">
            <Bot className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No Assessments Yet</h2>
            <p className="text-zinc-400">
              {selectedCourse 
                ? 'No learners have completed NAVI coaching sessions for this course.'
                : 'Select a course to view learner assessments.'}
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="h-5 w-5 text-navigate-gold" />
                  <span className="text-sm text-zinc-400">Learners Assessed</span>
                </div>
                <p className="text-2xl font-bold text-white">{report.total_learners}</p>
              </div>
              
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="h-5 w-5 text-blue-400" />
                  <span className="text-sm text-zinc-400">Avg Understanding</span>
                </div>
                <p className={`text-2xl font-bold ${getScoreColor(report.average_scores?.understanding)}`}>
                  {report.average_scores?.understanding}/10
                </p>
              </div>
              
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  <span className="text-sm text-zinc-400">Avg Application</span>
                </div>
                <p className={`text-2xl font-bold ${getScoreColor(report.average_scores?.application)}`}>
                  {report.average_scores?.application}/10
                </p>
              </div>
              
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="h-5 w-5 text-[#1EBFB3]" />
                  <span className="text-sm text-zinc-400">Avg Critical Thinking</span>
                </div>
                <p className={`text-2xl font-bold ${getScoreColor(report.average_scores?.critical_thinking)}`}>
                  {report.average_scores?.critical_thinking}/10
                </p>
              </div>
              
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Award className="h-5 w-5 text-navigate-gold" />
                  <span className="text-sm text-zinc-400">Avg Overall</span>
                </div>
                <p className={`text-2xl font-bold ${getScoreColor(report.average_scores?.overall)}`}>
                  {report.average_scores?.overall}/10
                </p>
              </div>
            </div>

            {/* Common Themes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Strengths */}
              {report.common_strengths?.length > 0 && (
                <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-green-400" />
                    Common Strengths
                  </h3>
                  <ul className="space-y-2">
                    {report.common_strengths.map((strength, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                        <span className="text-green-400 mt-1">+</span>
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Improvement Areas */}
              {report.common_improvement_areas?.length > 0 && (
                <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-amber-400" />
                    Areas for Development
                  </h3>
                  <ul className="space-y-2">
                    {report.common_improvement_areas.map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                        <span className="text-amber-400 mt-1">!</span>
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Individual Assessments */}
            <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-800">
                <h3 className="text-lg font-semibold text-white">Individual Learner Assessments</h3>
              </div>
              
              <div className="divide-y divide-zinc-800">
                {report.assessments?.map((assessment) => (
                  <div key={assessment.assessment_id} className="hover:bg-zinc-800/50 transition-colors">
                    {/* Assessment Row */}
                    <button
                      onClick={() => toggleAssessment(assessment.assessment_id)}
                      className="w-full flex items-center justify-between p-4 text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
                          <span className="text-navigate-gold font-semibold">
                            {(assessment.learner_name || 'L')[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-white">
                            {assessment.learner_name || 'Learner'}
                          </p>
                          <p className="text-sm text-zinc-400">
                            {assessment.module_title || assessment.course_title || 'Course Assessment'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className={`text-lg font-bold ${getScoreColor(assessment.overall_score)}`}>
                            {assessment.overall_score?.toFixed(1)}/10
                          </p>
                          <p className="text-xs text-zinc-500">Overall</p>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                          <Calendar className="h-4 w-4" />
                          {new Date(assessment.created_at).toLocaleDateString()}
                        </div>
                        {expandedAssessment === assessment.assessment_id ? (
                          <ChevronUp className="h-5 w-5 text-zinc-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-zinc-400" />
                        )}
                      </div>
                    </button>
                    
                    {/* Expanded Detail */}
                    {expandedAssessment === assessment.assessment_id && (
                      <div className="px-4 pb-4">
                        {loadingDetail ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="h-6 w-6 text-navigate-gold animate-spin" />
                          </div>
                        ) : detailedAssessment ? (
                          <div className="bg-zinc-900/50 rounded-lg p-6 space-y-6">
                            {/* Score Breakdown */}
                            <div className="grid grid-cols-4 gap-4">
                              {[
                                { label: 'Understanding', score: detailedAssessment.understanding_score },
                                { label: 'Application', score: detailedAssessment.application_score },
                                { label: 'Critical Thinking', score: detailedAssessment.critical_thinking_score },
                                { label: 'Communication', score: detailedAssessment.communication_score }
                              ].map((item) => (
                                <div key={item.label} className="text-center">
                                  <p className={`text-xl font-bold ${getScoreColor(item.score)}`}>
                                    {item.score}/10
                                  </p>
                                  <p className="text-xs text-zinc-400">{item.label}</p>
                                </div>
                              ))}
                            </div>
                            
                            {/* Instructor Summary */}
                            <div>
                              <h4 className="text-sm font-semibold text-navigate-gold mb-2 flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                Instructor Summary
                              </h4>
                              <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                                {detailedAssessment.instructor_summary}
                              </p>
                            </div>
                            
                            {/* Question Analyses */}
                            {detailedAssessment.question_analyses?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-white mb-3">
                                  Question-by-Question Analysis
                                </h4>
                                <div className="space-y-3">
                                  {detailedAssessment.question_analyses.map((qa, idx) => (
                                    <div key={idx} className="bg-zinc-800/50 rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-zinc-300">
                                          Q{qa.question_number}: {qa.topic}
                                        </span>
                                        <span className={`text-xs px-2 py-1 rounded border ${getCompetencyBadge(qa.competency_level)}`}>
                                          {qa.competency_level}
                                        </span>
                                      </div>
                                      <p className="text-sm text-zinc-400">
                                        {qa.learner_demonstrated}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Strengths & Areas */}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-sm font-semibold text-green-400 mb-2">Strengths</h4>
                                <ul className="space-y-1">
                                  {detailedAssessment.strengths?.map((s, idx) => (
                                    <li key={idx} className="text-sm text-zinc-300 flex items-start gap-2">
                                      <span className="text-green-400">+</span> {s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-amber-400 mb-2">Areas for Growth</h4>
                                <ul className="space-y-1">
                                  {detailedAssessment.areas_for_improvement?.map((a, idx) => (
                                    <li key={idx} className="text-sm text-zinc-300 flex items-start gap-2">
                                      <span className="text-amber-400">!</span> {a}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            
                            {/* Confidence Level */}
                            <div className="text-center pt-4 border-t border-zinc-700">
                              <span className="text-xs text-zinc-500">
                                Assessment Confidence: <span className="text-zinc-400 capitalize">{detailedAssessment.confidence_level}</span>
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
